import AddIcon from '@mui/icons-material/Add';
import CancelIcon from '@mui/icons-material/Cancel';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import Autocomplete from '@mui/material/Autocomplete';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import TextField from '@mui/material/TextField';
import {
  DataGrid,
  GridActionsCellItem,
  GridRowEditStopReasons,
  GridRowModes,
  GridToolbarContainer,
} from '@mui/x-data-grid';
import isEqual from 'lodash/isEqual';
import React, { useContext, useEffect, useReducer } from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';

import getSiteAttributes from '../../../../../api/oc_product/bulk/sites/site_attributes/gets/getSiteAttributes';
import getProductTypesAttribute from '../../../../../api/oc_product/productDataManagement/productTypes/get/getProductTypesAttribute';
import patchProductType from '../../../../../api/oc_product/productDataManagement/productTypes/patch/patchProductType';
import Modal from '../../../../../components/Modal';
import { ProductTypeContext } from '../../ProductTypeContext';

const reducer = (state, action) => {
  switch (action.type) {
    case 'INITIAL_STATE':
      return action.payload;
    case 'ID':
      return {
        ...state,
        id: action.payload,
      };
    case 'NAME':
      return {
        ...state,
        name: action.payload,
      };
    case 'ATTRIBUTE':
      return {
        ...state,
        attribute: action.payload,
      };
    default:
      return state;
  }
};

function EditToolbar(props) {
  const {
    setRows, setRowModesModel, setHasChanges, setActiveRowId,
  } = props;

  const handleClick = () => {
    const id = Math.random() * (1000000 - 1) + 1;
    setRows((oldRows) => [{
      id,
      name: '',
      productAttributeDefId: 0, // Default value for new row
      enabled: false,
      required: false,
      isAdded: true,
      isUpdated: false,
      isDeleted: false,
    }, ...oldRows]);
    setRowModesModel((oldModel) => ({
      ...oldModel,
      [id]: { mode: GridRowModes.Edit },
    }));
    setHasChanges(true);
    setActiveRowId(id);
  };
  return (
    <GridToolbarContainer>
      <Button
        color="primary"
        // disabled={activeRowId !== params.id}
        startIcon={<AddIcon />}
        onClick={() => handleClick()}
      >
        Add record
      </Button>
    </GridToolbarContainer>
  );
}

function EditModal({
  onClose, open, selectedRowData, updatedSelectedRowData,
}) {
  const {
    siteAttributeOptions,
    setSiteAttributeOptions,
    siteId,
  } = useContext(ProductTypeContext);
  const [hasChanges, setHasChanges] = React.useState(false);
  const [disableBtn, setDisableBtn] = React.useState(true);
  const [updatedSiteAttributes, setUpdatedSiteAttributes] = React.useState([]);
  const [selectedProductAttributeDefId, setSelectedProductAttributeDefId] = React.useState(null);
  const [filterSiteAttributeOptions, setFilterSiteAttributeOptions] = React.useState([]);
  const languageId = useSelector((state) => state.user.loginResponse?.selectedSite?.LanguageId);
  const [initialFormState, setInitialFormState] = React.useState({});

  const [state, dispatch] = useReducer(reducer, selectedRowData);
  const [rows, setRows] = React.useState([]);
  const [rowModesModel, setRowModesModel] = React.useState({});
  const [originalRows, setOriginalRows] = React.useState([]);
  const [editName, setEditName] = React.useState('');
  const [editDepartmentCode, setEditDepartmentCode] = React.useState('');
  const [editIsDefault, setEditIsDefault] = React.useState(false);
  const [searchValue, setSearchValue] = React.useState('');
  const [deletedRows, setDeletedRows] = React.useState([]);
  const [activeRowId, setActiveRowId] = React.useState(null);

  const updateDisableBtn = () => {
    if (!open) {
      setDisableBtn(true);
      return;
    }
    const isInEditMode = Object.values(rowModesModel).some(
      (mode) => mode.mode === GridRowModes.Edit,
    );

    const hasNameChanges = selectedRowData.name !== editName;
    const hasDepartmentCodeChanges = selectedRowData.departmentCode !== editDepartmentCode;
    const hasIsDefaultChanges = selectedRowData.default !== editIsDefault;
    const hasAttributeChanges = updatedSiteAttributes.length > 0
      || deletedRows.length > 0;

    const finalDisableState = isInEditMode
      || hasAttributeChanges
      || hasNameChanges
      || hasIsDefaultChanges
      || hasDepartmentCodeChanges;
    setDisableBtn(finalDisableState);
    if (finalDisableState) {
      setDisableBtn(false);
    } else {
      setDisableBtn(true);
    }
  };
  const handleClose = () => {
    onClose(false);
    setRows([]);
    setDeletedRows([]);
    setUpdatedSiteAttributes([]);
    setDisableBtn(true);
    setRowModesModel({});
    setHasChanges(false);
    setDisableBtn(true);
  };
  const handleRowEditStop = (params, event) => {
    if (params.reason === GridRowEditStopReasons.rowFocusOut) {
      // eslint-disable-next-line no-param-reassign
      event.defaultMuiPrevented = true;
    }
  };

  const siteAttributeOptionsFormatted = siteAttributeOptions.map((option) => ({
    value: option.ProductAttributeDefId,
    label: option.AttributeName,
  }));
  const handleSaveClick = (params) => async () => {
    const isNewRow = rows.find((row) => row.id === params.id)?.isAdded;
    const updatedRows = rows.map((row) => {
      if (row.id === params.id) {
        if (isNewRow) {
          return {
            ...row,
            name: params.row.name.split('-')[0],
            productAttributeDefId: selectedProductAttributeDefId,
            enabled: params.row.enabled,
            required: params.row.required,
            productTypeAttributeId: params.row.productTypeAttributeId,
            isAdded: true,
            isUpdated: false,
            isDeleted: false,
          };
        }
        return {
          ...row,
          enabled: params.row.enabled,
          required: params.row.required,
          productTypeAttributeId: params.row.productTypeAttributeId,
          isAdded: false,
          isUpdated: true,
          isDeleted: false,
        };
      }
      return row;
    });
    setActiveRowId(null);
    setUpdatedSiteAttributes(updatedRows);
    setRows(updatedRows);
    setRowModesModel({ ...rowModesModel, [params.id]: { mode: GridRowModes.View } });
    setHasChanges(true);
    setDisableBtn(false);
    updateDisableBtn();
  };

  const handleEditClick = (id) => async () => {
    const clickedRow = rows.find((row) => row.id === id);
    const updatedRows = rows.map((row) => {
      if (row.id === id && !row.isAdded) {
        return {
          ...row,
          enabled: clickedRow.enabled,
          required: clickedRow.required,
          isAdded: false,
          isUpdated: true,
          isDeleted: false,
        };
      }
      if (row.id === id && row.isAdded) {
        return {
          ...row,
          productTypeAttributeId: clickedRow.productTypeAttributeId,
          productAttributeDefId: selectedProductAttributeDefId,
          enabled: clickedRow.enabled,
          required: clickedRow.required,
          isAdded: true,
          isUpdated: false,
          isDeleted: false,
        };
      }
      return row;
    });
    setActiveRowId(id);
    setRows(updatedRows);
    setHasChanges(true);
    setDisableBtn(false);
    setUpdatedSiteAttributes(updatedRows);
    setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.Edit } });
  };

  const handleDeleteClick = (id) => () => {
    const clickedRow = rows.find((row) => row.id === id);
    if (!clickedRow) return;
    const deleted = {
      ...clickedRow,
      productTypeAttributeId: clickedRow.productTypeAttributeId,
      isAdded: false,
      isUpdated: false,
      isDeleted: true,
    };

    setDeletedRows((prev) => [...prev, deleted]);
    setRows(rows.filter((row) => row.id !== id));
    setHasChanges(true);
    setDisableBtn(false);
  };
  const checkFormChanges = (updatedField, updatedValue) => {
    if (!initialFormState) {
      setHasChanges(false);
      return;
    }

    const newFormState = {
      ...state,
      [updatedField]: updatedValue,
    };

    const hasAnyChange = Object.keys(initialFormState).some((key) => newFormState[key] !== initialFormState[key]);

    setHasChanges(hasAnyChange);
  };

  useEffect(() => {
    checkFormChanges();
  }, [state]);

  const handleCancelClick = (id) => () => {
    console.log(state);

    setRowModesModel({
      ...rowModesModel,
      [id]: { mode: GridRowModes.View, ignoreModifications: true },
    });

    const editedRow = rows.find((row) => row.id === id);
    if (editedRow.isAdded) {
      setRows(rows.filter((row) => row.id !== id));
    }
    setActiveRowId(null);
  };

  const processRowUpdate = async (newRow) => {
    const updatedRow = {
      ...newRow, isAdded: newRow.isAdded, isUpdated: newRow.isUpdated, isDeleted: newRow.isDeleted,
    };
    const originalRow = originalRows.find(
      (row) => row.productAttributeDefId === newRow.productAttributeDefId,
    );

    let isSameAsOriginal = false;
    if (originalRow) {
      isSameAsOriginal = originalRow.productAttributeDefId === newRow.productAttributeDefId
        && originalRow.enabled === newRow.enabled
        && originalRow.required === newRow.required;
    }
    if (newRow.productAttributeDefId) {
      if (isSameAsOriginal) {
        // Remove from updateSiteAttributes if identical to original
        setUpdatedSiteAttributes((prev) => prev.filter(
          (option) => option.productAttributeDefId
            !== newRow.productAttributeDefId,
        ));
      } else {
        // Add or update in SiteAttributes
        const modifiedRow = {
          productTypeAttributeId: newRow.productTypeAttributeId,
          productAttributeDefId: newRow.productAttributeDefId,
          name: newRow.name.split('-')[0],
          enabled: newRow.enabled,
          required: newRow.required,
          isAdded: newRow.isAdded,
          isUpdated: newRow.isUpdated,
          isDeleted: newRow.isDeleted,
        };
        setUpdatedSiteAttributes((prev) => {
          const index = prev.findIndex(
            (option) => option.productAttributeDefId
              === modifiedRow.productAttributeDefId,
          );
          if (index !== -1) {
            const updatedOptions = [...prev];
            updatedOptions[index] = modifiedRow;
            return updatedOptions;
          }
          return [...prev, modifiedRow];
        });
      }
    } else {
      // Handle newly created rows as before
      const modifiedRow = {
        id: newRow.id,
        name: newRow.name.split('-')[0],
        productAttributeDefId: newRow.productAttributeDefId,
        productTypeAttributeId: newRow.productTypeAttributeId,
        enabled: newRow.enabled,
        required: newRow.required,
        isAdded: newRow.isAdded,
        isUpdated: newRow.isUpdated,
        isDeleted: newRow.isDeleted,
      };
      setUpdatedSiteAttributes((prev) => {
        const index = prev.findIndex((option) => option.id === modifiedRow.id);
        if (index !== -1) {
          const updatedOptions = [...prev];
          updatedOptions[index] = modifiedRow;
          return updatedOptions;
        }
        return [...prev, modifiedRow];
      });
    }
    setRows((prev) => prev.map((row) => (row.id === newRow.id ? updatedRow : row)));
    updateDisableBtn();
    setHasChanges(true);
    return updatedRow;
  };
  useEffect(() => {
    updateDisableBtn();
  }, [
    hasChanges,
    // rowModesModel,
    // state,
    // open,
    updatedSiteAttributes,
    editName,
    editDepartmentCode,
    editIsDefault,
  ]);
  const handleRowChange = (id, field, value) => {
    setRows((prevRows) => prevRows.map((row) => (row.id === id ? { ...row, [field]: value } : row)));
  };
  const columns = [
    {
      field: 'name',
      headerName: 'Name',
      width: 550,
      type: 'singleSelect',
      headerAlign: 'center',
      align: 'center',
      valueOptions: siteAttributeOptionsFormatted,
      renderCell: (params) => {
        const isNewRow = params.row.isAdded;
        const isInEditMode = rowModesModel[params.id]?.mode === GridRowModes.Edit;
        return isNewRow && isInEditMode ? (
          <Autocomplete
            id="attribute-autocomplete"
            size="small"
            options={filterSiteAttributeOptions}
            getOptionLabel={(option) => (option ? option.AttributeName.split('-')[0] : '')}
            onChange={(e, value) => {
              handleRowChange(params.id, 'name', value.AttributeName);
              handleRowChange(params.id, 'productTypeAttributeId', value.ProductTypeAttributeId);
              setSelectedProductAttributeDefId(value.ProductAttributeDefId);
              setFilterSiteAttributeOptions((prev) => prev.filter((option) => option.ProductAttributeDefId !== value.ProductAttributeDefId));
            }}
            // eslint-disable-next-line no-shadow
            renderInput={(params) => (
              <TextField
                {...params}
                label="Attributes"
                placeholder="Select an attribute"
                id="attribute-textfield"
              />
            )}
            className="w-full"
          />
        ) : (
          <div>
            {params.value}
          </div>
        );
      },
    },
    {
      field: 'enabled',
      headerName: 'Enabled',
      width: 100,
      headerAlign: 'center',
      renderCell: (params) => {
        const isInEditMode = rowModesModel[params.id]?.mode === GridRowModes.Edit;
        // eslint-disable-next-line no-nested-ternary
        return isInEditMode ? (
          <Checkbox
            checked={params.row.enabled}
            onChange={(event) => handleRowChange(params.id, 'enabled', event.target.checked)}
          />
        ) : params.row.enabled ? (
          <CheckCircleIcon className="text-success-600" />
        ) : (
          <CancelIcon className="text-error-600" />
        );
      },
    },
    {
      field: 'required',
      headerName: 'Required',
      width: 100,
      headerAlign: 'center',
      renderCell: (params) => {
        const isInEditMode = rowModesModel[params.id]?.mode === GridRowModes.Edit;
        // eslint-disable-next-line no-nested-ternary
        return isInEditMode ? (
          <Checkbox
            checked={params.row.required}
            onChange={(event) => handleRowChange(params.id, 'required', event.target.checked)}
          />
        ) : params.row.required ? (
          <CheckCircleIcon className="text-success-600" />
        ) : (
          <CancelIcon className="text-error-600" />
        );
      },
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Actions',
      width: 100,
      cellClassName: 'actions',
      editable: false,
      getActions: (params) => {
        const isInEditMode = rowModesModel[params.id]?.mode === GridRowModes.Edit;
        if (isInEditMode) {
          return [
            <GridActionsCellItem
              icon={<SaveIcon />}
              label="Save"
              sx={{
                color: 'primary.main',
              }}
              onClick={handleSaveClick(params)}
              disabled={activeRowId !== params.id}
            />,
            <GridActionsCellItem
              icon={<CancelIcon />}
              label="Cancel"
              className="textPrimary"
              onClick={handleCancelClick(params.id)}
              color="inherit"
              disabled={activeRowId !== params.id}
            />,
          ];
        }
        const isActiveRow = activeRowId === null || activeRowId === params.id;
        return [
          <GridActionsCellItem
            icon={<EditIcon />}
            label="Edit"
            className="textPrimary"
            onClick={handleEditClick(params.id)}
            color="inherit"
            disabled={!isActiveRow || rowModesModel[params.id]?.mode === GridRowModes.Edit}
          />,
          <GridActionsCellItem
            icon={<DeleteIcon />}
            label="Delete"
            onClick={handleDeleteClick(params.id)}
            color="inherit"
          />,
        ];
      },
    },
  ];

  useEffect(() => {
    const initialState = {
      id: selectedRowData?.id,
      attributeDefKey: selectedRowData?.row?.productAttributeDefKey,
      name: selectedRowData?.row?.eName,
      attributeDataType: selectedRowData?.row?.attributeDataType?.description,
    };
    dispatch({ type: 'INITIAL_STATE', payload: initialState });
    setInitialFormState(initialState); // <-- set initial form state
    setEditName(selectedRowData?.name || '');
    setEditDepartmentCode(selectedRowData?.departmentCode || '');
    setEditIsDefault(selectedRowData?.default);
    const getAllAttributes = async () => {
      try {
        const [response1, response2, response3] = await Promise.all([
          getSiteAttributes(siteId, 2, languageId),
          getSiteAttributes(siteId, 3, languageId),
          getSiteAttributes(siteId, 1, languageId),
        ]);
        const siteAttributes = [
          ...response1.data,
          ...response2.data,
          ...response3.data,
        ];
        setSiteAttributeOptions(siteAttributes);
      } catch (err) {
        console.error(err);
      }
    };
    getAllAttributes();
  }, [selectedRowData, open]);

  React.useEffect(() => {
    if (searchValue !== '') {
      const filteredRows = originalRows.filter((row) => (row.name.toLowerCase().includes(searchValue.toLowerCase())));
      setRows(filteredRows);
    } else {
      setRows(originalRows);
    }
  }, [searchValue, originalRows]);

  React.useEffect(() => {
    if (searchValue.length > 0) {
      setSearchValue('');
    }
  }, [siteId]);

  React.useEffect(() => {
    const fetchProductData = async () => {
      try {
        // Fetch product attributes based on ProductTypeId
        const productTypesData = await getProductTypesAttribute(selectedRowData.productTypeId);
        const mappedRows = productTypesData.data.map((item, index) => ({
          id: index + 1,
          productAttributeDefId: item.ProductAttributeDefId,
          productTypeAttributeId: item.ProductTypeAttributeId,
          name: item.AttributeName,
          enabled: item.Enabled,
          required: item.Required,
          isDeleted: false,
          isUpdated: false,
          isAdded: false,
        }));
        setRows(mappedRows);
        setOriginalRows(mappedRows);
        setFilterSiteAttributeOptions(siteAttributeOptions.filter((option) => (
          !mappedRows.some((row) => row.productAttributeDefId === option.ProductAttributeDefId))));
      } catch (error) {
        console.error('Error fetching product types attribute data:', error);
      }
    };

    if (open) {
      fetchProductData();
    }
  }, [open, siteId]);

  const handleRowModesModelChange = (newRowModesModel) => {
    setRowModesModel(newRowModesModel);
  };
  useEffect(() => {
    if (open) {
      setDisableBtn(true);
      setHasChanges(false);
    }
  }, [open]);

  const saveProductType = async () => {
    const sortDeletedRows = deletedRows.filter((row) => row.isDeleted === true);
    const updatedSiteAttribute1 = [...updatedSiteAttributes, ...sortDeletedRows];
    const body = {
      siteId,
      productTypeId: selectedRowData.productTypeId,
      productTypeName: editName,
      departmentCode: editDepartmentCode,
      default: editIsDefault,
      attribute: updatedSiteAttribute1.filter((row) => row.isUpdated !== false || row.isAdded !== false || row.isDeleted !== false),
    };
    const updatedSelectedRow = {
      ...selectedRowData,
      name: editName,
      departmentCode: editDepartmentCode,
      default: editIsDefault,
    };
    updatedSelectedRowData(updatedSelectedRow);
    try {
      const res = await patchProductType(body);
      if (res) {
        toast.success('Successfully saved Product Type');
      }
    } catch (error) {
      console.error('Error during save::', error);
    } finally {
      handleClose();
    }
  };
  useEffect(() => {
    if (!initialFormState) {
      setHasChanges(false);
      return;
    }

    const hasAnyChange = !isEqual(state, initialFormState);
    setHasChanges(hasAnyChange);
  }, [state, initialFormState]);

  return (
    <Modal
      open={open}
      title="Edit Product Types & Attributes"
      buttonLabel="Save"
      disableButton={disableBtn}
      buttonOnClick={saveProductType}
      onClose={() => {
        // Set all rows back to View mode before closing the modal
        const resetRowModesModel = {};
        Object.keys(rowModesModel).forEach((id) => {
          resetRowModesModel[id] = { mode: GridRowModes.View };
        });
        setRowModesModel(resetRowModesModel);
        handleClose();
        setActiveRowId(null);
      }}
      desktopModalWidth="50%"
      desktopModalHeight="80%"
      containerWidth="xl"
    >
      <Grid container spacing={2} className="max-h-[250px]" justifyContent="end">
        <Grid item xs={6} className="min-w-[100%]">
          <TextField
            label="Name"
            variant="outlined"
            size="small"
            value={editName}
            onChange={(e) => {
              setEditName(e.target.value);
              setDisableBtn(false);
            }}
            className="w-full mb-8"
          />
          <TextField
            label="Department Code"
            variant="outlined"
            size="small"
            value={editDepartmentCode}
            onChange={(e) => {
              setEditDepartmentCode(e.target.value);
              setDisableBtn(false);
            }}
            className="w-full mb-8"
          />
          <p className="text-[14px]">Is Default</p>
          <Checkbox
            onClick={(event) => {
              setEditIsDefault(event.target.checked);
              setDisableBtn(false);
            }}
            checked={editIsDefault}
            className="mr-0"
          />
        </Grid>
      </Grid>
      <Divider
        orientation="vertical"
        variant="middle"
        flexItem
        className="mx-10"
      />
      <Grid container className="min-w-[600px]" spacing={2}>
        <Grid item xs={12} container justifyContent="space-between" alignItems="center">
          <TextField
            label="Search by Product Name Attribute"
            value={searchValue}
            onChange={(event) => setSearchValue(event.target.value)}
            variant="outlined"
            size="small"
            className="mr-6 w-[300px]"
          />
        </Grid>
        <Grid item xs={12}>
          <DataGrid
            columns={columns.map((column) => ({
              ...column,
              // editable: isRowEditable,
            }))}
            rows={rows}
            initialState={{
              pagination: {
                paginationModel: {
                  pageSize: 15,
                },
              },
            }}
            className="bg-white cursor-pointer min-h-[320px] max-h-[320px]"
            editMode="row"
            onRowModesModelChange={handleRowModesModelChange}
            rowModesModel={rowModesModel}
            onRowEditStop={handleRowEditStop}
            processRowUpdate={processRowUpdate}
            onProcessRowUpdateError={(error) => {
              console.error('Error during row update:', error);
            }}
            slots={{
              toolbar: EditToolbar,
            }}
            slotProps={{
              toolbar: {
                setRows, setRowModesModel, setHasChanges, setActiveRowId, setFilterSiteAttributeOptions,
              },
            }}
            sx={{
              '.MuiDataGrid-cell--withRenderer:nth-child(2), .MuiDataGrid-cell--withRenderer:nth-child(3)': {
                justifyContent: 'center',
              },
            }}
          />
        </Grid>
      </Grid>
    </Modal>
  );
}

export default EditModal;
